import React, { useMemo } from "react";
import { employeeHistoryApi } from "../../services/employeeApi";
import { timekeepingApi } from "../../services/timekeepingApi";
import { leaveApi } from "../../services/leaveApi";
import { trainingApi } from "../../services/trainingApi";
import { usePeriodSection } from "../../hooks/usePeriodSection";
import { buildMonthlyTimeline } from "../../lib/monthlyRecordTimeline";
import { MonthlySummary } from "./monthlyRecord/MonthlySummary";
import { ScheduleSection } from "./monthlyRecord/ScheduleSection";
import { AvailabilitySection } from "./monthlyRecord/AvailabilitySection";
import { TimecardSection } from "./monthlyRecord/TimecardSection";
import { ApprovedAttendanceSection } from "./monthlyRecord/ApprovedAttendanceSection";
import { ReconciliationSection } from "./monthlyRecord/ReconciliationSection";
import { PayrollSection } from "./monthlyRecord/PayrollSection";
import { LeaveSection } from "./monthlyRecord/LeaveSection";
import { TrainingSection } from "./monthlyRecord/TrainingSection";
import { ComplianceSection } from "./monthlyRecord/ComplianceSection";
import { DocumentsSection } from "./monthlyRecord/DocumentsSection";
import { AccountAccessSection } from "./monthlyRecord/AccountAccessSection";
import { UnifiedTimeline } from "./monthlyRecord/UnifiedTimeline";

/** Employee Profile - "Monthly Record" tab (Phase 2). Orchestrates ~8 independent,
 * period-scoped fetches in parallel (never one giant blocking call - see
 * usePeriodSection) and feeds their results both to their own section card AND to the
 * Unified Timeline builder, so no section is ever fetched twice. `employee`/`contract`/
 * `notice`/`medicalCheck` are passed down from EmployeeProfilePage, which already holds
 * them - Documents/Compliance/Account Access never issue their own network request. */
export const MonthlyRecordTab = ({ employeeId, period, employee, contract, notice, medicalCheck, perm }) => {
  const schedule = usePeriodSection(() => employeeHistoryApi.schedule(employeeId, period), [employeeId, period]);
  const availability = usePeriodSection(() => employeeHistoryApi.availability(employeeId, period), [employeeId, period]);
  const attendance = usePeriodSection(() => timekeepingApi.getEmployeeMonthReview(employeeId, period), [employeeId, period]);
  const reconciliation = usePeriodSection(() => employeeHistoryApi.reconciliation(employeeId, period), [employeeId, period]);
  const payroll = usePeriodSection(() => employeeHistoryApi.payroll(employeeId, period), [employeeId, period], { enabled: perm.canViewPayroll });
  const leave = usePeriodSection(() => leaveApi.employeeLedger(employeeId), [employeeId], { enabled: perm.canViewLeave });
  const training = usePeriodSection(() => trainingApi.employeeProfile(employeeId), [employeeId], { enabled: perm.canViewTraining });

  const timeline = useMemo(() => buildMonthlyTimeline({
    period,
    schedule: schedule.data, availability: availability.data, attendance: attendance.data,
    payroll: payroll.data, leaveLedger: leave.data, training: training.data,
    medicalCheck, contract, notice,
  }), [period, schedule.data, availability.data, attendance.data, payroll.data, leave.data, training.data, medicalCheck, contract, notice]);

  return (
    <div className="space-y-4" data-testid="monthly-record-tab">
      <MonthlySummary
        schedule={schedule.data} attendance={attendance.data} reconciliation={reconciliation.data}
        payroll={payroll.data} leaveLedger={leave.data} medicalCheck={medicalCheck} period={period}
      />

      <ScheduleSection data={schedule.data} loading={schedule.loading} error={schedule.error} onRetry={schedule.retry} />
      <AvailabilitySection data={availability.data} loading={availability.loading} error={availability.error} onRetry={availability.retry} />
      <TimecardSection employeeId={employeeId} data={attendance.data} loading={attendance.loading} error={attendance.error} onRetry={attendance.retry} />
      <ApprovedAttendanceSection data={attendance.data} loading={attendance.loading} error={attendance.error} onRetry={attendance.retry} />
      <ReconciliationSection data={reconciliation.data} loading={reconciliation.loading} error={reconciliation.error} onRetry={reconciliation.retry} />

      {perm.canViewPayroll && (
        <PayrollSection data={payroll.data} loading={payroll.loading} error={payroll.error} onRetry={payroll.retry} />
      )}
      {perm.canViewLeave && (
        <LeaveSection data={leave.data} period={period} loading={leave.loading} error={leave.error} onRetry={leave.retry} />
      )}
      {perm.canViewTraining && (
        <TrainingSection data={training.data} period={period} loading={training.loading} error={training.error} onRetry={training.retry} />
      )}
      {perm.canManageMedicalChecks && <ComplianceSection medicalCheck={medicalCheck} period={period} />}

      <DocumentsSection contract={contract} notice={notice} period={period} />
      <AccountAccessSection employee={employee} />

      <UnifiedTimeline events={timeline} />
    </div>
  );
};
